import * as Sentry from '@sentry/react'
import React from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import CONSTANTS from 'utils/constants'
import type { SentryEnv } from '../../vite-env.d.ts'
import { isAbortError, isApiError } from './apiError'
/**
 * Initialise sentry, used for
 * - Crash analytics
 * - Performance monitoring
 * - Debugging
 */
export const initialiseSentry = () => {
  const sentryEnv = CONSTANTS.VITE_SENTRY_ENV
  const { tracesSampleRate } = getEnvProps(sentryEnv)

  Sentry.init({
    enabled: CONSTANTS.VITE_SENTRY_ENABLED,
    dsn: 'https://b7b7bcf682aa4790a25d3bebc0f46731@o1072246.ingest.sentry.io/6070751',
    beforeSend(event, hint) {
      if (shouldDropEvent(hint?.originalException)) {
        return null
      }

      // Get the URL from the event
      const url = event.request?.url

      // If the URL exists, sanitize and filter it
      if (url && event.request?.url) {
        const sanitizedUrl = sanitizeAndFilterUrl(url)
        // If the sanitized URL is not null, set it as the new URL
        if (sanitizedUrl) {
          event.request.url = sanitizedUrl
        }
      }

      return event
    },
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),
      Sentry.replayIntegration()
    ],

    // tracesSampleRate at 1.0 to captures 100%
    // of transactions for performance monitoring
    // in our development environment
    tracesSampleRate,
    normalizeDepth: 10,
    environment: sentryEnv,
    release: CONSTANTS.VITE_RELEASE_NAME
  })

  return Sentry.withSentryReactRouterV6Routing(Routes)
}

type EnvProps = {
  tracesSampleRate: number
}
const getEnvProps = (env: SentryEnv): EnvProps => {
  const tracesSampleRate = getTracesSampleRate(env)

  return {
    tracesSampleRate
  }
}

const getTracesSampleRate = (env: SentryEnv): number => {
  switch (env) {
    case 'Dev':
      return 0
    case 'Staging':
      return 0.1
    case 'Prod':
      return 0.01
    default:
      return 0
  }
}

export type SentryFeatureArea = 'emar' | 'rota' | 'careManagement'

/**
 * Rota/rostering screens (from `screens/Rostering`) that are mounted outside
 * the `rota/*` prefix — under `settings/`, `residents/:id/settings/`,
 * `organisation/team/:id/`, and `reports/rostering/*` — because the module
 * boundary doesn't match the URL structure. Checked as exact path segments.
 */
const ROTA_PATH_SEGMENTS = [
  'rostering',
  'care-package',
  'care-package-and-patterns',
  'contracts-and-absences',
  'contract-and-preferences',
  'warnings',
  'rota-patterns',
  'travel-time-and-mileage',
  'geofencing',
  'pay-rates',
  'finance',
  'absences',
  'all-absences'
]

/**
 * Maps the current route to a coarse feature area so Sentry issues can be
 * filtered by product area. Emar routes are nested under `residents/:id/...`
 * rather than being a sibling of it (including a nested `emar-activation`
 * paywall route distinct from the top-level one), so they're matched before
 * the careManagement fallback, which covers everything else in the app.
 */
export function getFeatureAreaFromPathname(
  pathname: string
): SentryFeatureArea {
  const segments = pathname.replace(/^\/+/, '').split('/')

  if (segments[0] === 'rota') return 'rota'
  if (ROTA_PATH_SEGMENTS.some((segment) => segments.includes(segment))) {
    return 'rota'
  }
  if (segments.includes('emar-activation') || segments.includes('emar')) {
    return 'emar'
  }

  return 'careManagement'
}

/**
 * Drops 404s and aborted requests. A 404 belongs in the backend's logs rather
 * than in frontend crash reporting (CM-2340), and an abort is a request we
 * cancelled ourselves.
 */
export function shouldDropEvent(originalException: unknown): boolean {
  if (isAbortError(originalException)) return true

  return isApiError(originalException) && originalException.status === 404
}

// Define a custom route matcher function
function sanitizeAndFilterUrl(url: string): string | null {
  // Define the pattern for MongoDB ObjectId identifiers
  const objectIdPattern = /[0-9a-fA-F]{24}/

  // Replace all occurrences of MongoDB ObjectId identifiers with a placeholder
  const sanitizedUrl = url.replace(objectIdPattern, ':id')

  // Return the sanitized URL
  return sanitizedUrl
}

export default initialiseSentry
